import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { loadHdChartGlobalSettings } from './chart-tabs/hd-chart-global-settings';
import {
  HD_BODYGRAPH_DEFAULT_COLOR_CONFIG,
  HdBodygraphColorConfig,
  resolveHdBodygraphCenterTextColor,
} from './hd-bodygraph-color-config';
import { HdBodygraphCenter } from './hd-bodygraph.models';
import { HD_CENTER_ICON_SVG_STYLE, buildHdCenterShapeSvg } from './hd-center-shapes';

/**
 * Het kleurenpalet dat de gebruiker gekozen heeft, voor alles wat geen palet
 * meegegeven krijgt: miniatuur-charts (geboortevenster, chartbeheer, posters),
 * center-iconen, brugpoort-badges, kleurstrips. De settings-store registreert
 * zijn `colorConfig`-signal, zodat een paletwissel overal meteen doorwerkt;
 * zolang dat niet gebeurd is (of buiten de HD-app) geldt het opgeslagen palet.
 * Bewust zonder store-injectie: kleine componenten slepen zo de store en zijn
 * afhankelijkheden niet mee.
 */
@Injectable({ providedIn: 'root' })
export class HdActivePaletteService {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly colorConfigSource = signal<Signal<HdBodygraphColorConfig> | undefined>(undefined);
  private readonly storedColorConfig = loadHdChartGlobalSettings().colorConfig ?? HD_BODYGRAPH_DEFAULT_COLOR_CONFIG;
  private readonly iconCache = new Map<string, SafeHtml>();

  /** Volledig bodygraph-palet (centers, kanalen, poortcirkels, ...). */
  readonly colorConfig = computed<HdBodygraphColorConfig>(
    () => this.colorConfigSource()?.() ?? this.storedColorConfig,
  );

  readonly centerColors = computed<Readonly<Record<HdBodygraphCenter, string>>>(
    () => this.colorConfig().centerDefined,
  );

  registerColorConfig(colorConfig: Signal<HdBodygraphColorConfig>): void {
    this.colorConfigSource.set(colorConfig);
  }

  color(center: HdBodygraphCenter): string {
    return this.centerColors()[center];
  }

  /** Bijna-wit op een donkere centerkleur, anders donkere tekst. */
  textColor(center: HdBodygraphCenter): string {
    return resolveHdBodygraphCenterTextColor(this.color(center));
  }

  /** Gevuld center-icoon in de paletkleur, gememoïseerd per center, kleur en sizing. */
  iconHtml(center: HdBodygraphCenter, svgStyle: string = HD_CENTER_ICON_SVG_STYLE): SafeHtml {
    const fill = this.color(center);
    const cacheKey = `${center}|${fill}|${svgStyle}`;
    const cached = this.iconCache.get(cacheKey);
    if (cached != null) {
      return cached;
    }
    const html = this.sanitizer.bypassSecurityTrustHtml(buildHdCenterShapeSvg(center, `fill="${fill}"`, svgStyle));
    this.iconCache.set(cacheKey, html);
    return html;
  }
}
